import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CustomTypography } from "../../custom-typography/custom-typography";
import {
  deleteAssessment,
  getActualAssessmentList,
  type ActualAssessmentListItem,
} from "../../../axios/admin";
import { AdminCreds } from "../../../types";
import { getAssessmentApiErrorMessage } from "../upload-assessment-modal/assessment-api-error";
import {
  buttonsContainerStyles,
  contestItemStyles,
  contestMetaTypographyStyles,
  contestTitleTypographyStyles,
  deleteButtonStyles,
  emptyListTypographyStyles,
  errorTypographyStyles,
  fieldContainerStyles,
  headerTypographyStyles,
  hintTypographyStyles,
  listContainerStyles,
  listLoadingStyles,
  modalBoxStyles,
  secondaryButtonStyles,
  successTypographyStyles,
} from "./styles";

interface DeleteContestModalProps {
  open: boolean;
  onClose: () => void;
  adminCreds: AdminCreds;
}

const formatStartTime = (value?: string): string => {
  if (!value) return "—";
  const parsed = dayjs(value.replace("Z", "+00:00"));
  return parsed.isValid() ? parsed.format("DD.MM.YYYY HH:mm UTC") : value;
};

export const DeleteContestModal = ({
  open,
  onClose,
  adminCreds,
}: DeleteContestModalProps) => {
  const [contests, setContests] = useState<ActualAssessmentListItem[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadContests = useCallback(async () => {
    setListLoading(true);
    setFormError(null);
    try {
      const list = await getActualAssessmentList(adminCreds);
      setContests(list);
      setSelectedId((prev) =>
        list.some((item) => item.id === prev) ? prev : ""
      );
    } catch (error) {
      const errorMessage = getAssessmentApiErrorMessage(
        error,
        "Oops... Unknown error happened while loading contests"
      );
      console.error("Error loading contests:", errorMessage);
      setFormError(errorMessage);
      setContests([]);
      setSelectedId("");
    } finally {
      setListLoading(false);
    }
  }, [adminCreds.adminLogin, adminCreds.adminPassword]);

  useEffect(() => {
    if (!open) return;
    void loadContests();
  }, [open, loadContests]);

  const handleClose = () => {
    setContests([]);
    setSelectedId("");
    setFormError(null);
    setSuccessMessage(null);
    setListLoading(false);
    setDeleteLoading(false);
    onClose();
  };

  const handleDelete = async () => {
    const trimmedId = selectedId.trim();
    if (!trimmedId) {
      setFormError("Select a contest to delete");
      return;
    }

    setDeleteLoading(true);
    setFormError(null);
    setSuccessMessage(null);

    try {
      const res = await deleteAssessment(trimmedId, adminCreds);
      const message =
        typeof res === "string" && res.trim()
          ? res
          : "Contest deleted successfully";
      setSuccessMessage(message);
      setSelectedId("");
      await loadContests();
    } catch (error) {
      const errorMessage = getAssessmentApiErrorMessage(
        error,
        "Oops... Unknown error happened while deleting contest"
      );
      console.error("Error deleting contest:", errorMessage);
      setFormError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalBoxStyles}>
        <Box sx={fieldContainerStyles}>
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={headerTypographyStyles}
          >
            Delete contest
          </CustomTypography>

          <Typography variant="body2" sx={hintTypographyStyles}>
            Showing not-finished contests. Delete works only for the author and
            only before the contest has started.
          </Typography>

          {formError && (
            <Typography variant="body2" sx={errorTypographyStyles}>
              {formError}
            </Typography>
          )}

          {successMessage && (
            <Typography variant="body2" sx={successTypographyStyles}>
              {successMessage}
            </Typography>
          )}
        </Box>

        <Box sx={listContainerStyles}>
          {listLoading ? (
            <Box sx={listLoadingStyles}>
              <CircularProgress size={28} />
            </Box>
          ) : contests.length === 0 ? (
            <Typography variant="body2" sx={emptyListTypographyStyles}>
              No active contests found.
            </Typography>
          ) : (
            contests.map((contest) => {
              const isSelected = selectedId === contest.id;
              return (
                <Box
                  key={contest.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelectedId(contest.id);
                    setFormError(null);
                    setSuccessMessage(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedId(contest.id);
                      setFormError(null);
                      setSuccessMessage(null);
                    }
                  }}
                  sx={contestItemStyles(isSelected)}
                >
                  <CustomTypography
                    fontSize={14}
                    fontWeight={600}
                    sx={contestTitleTypographyStyles}
                  >
                    {contest.title}
                  </CustomTypography>
                  <Typography variant="body2" sx={contestMetaTypographyStyles}>
                    id: {contest.id}
                    {contest.focus ? ` · ${contest.focus}` : ""}
                  </Typography>
                  <Typography variant="body2" sx={contestMetaTypographyStyles}>
                    start: {formatStartTime(contest.time_to_send)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>

        <Box sx={buttonsContainerStyles}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={secondaryButtonStyles}
            disabled={deleteLoading}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleDelete()}
            sx={deleteButtonStyles}
            disabled={deleteLoading || listLoading || !selectedId}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
